import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, Mail, Phone, User, Plus, Landmark, MapPin, Receipt, CreditCard, Minus, Trash2, Check, X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useState } from "react";
import { ContactForm } from "@/components/ContactForm";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface EditableCell {
  index?: number;
  field: string;
  value: string;
}

export default function Client() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numericClientId = clientId ? parseInt(clientId, 10) : undefined;
  const [editingCell, setEditingCell] = useState<EditableCell | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");

  const { data: client, isLoading, refetch } = useQuery({
    queryKey: ['client', numericClientId],
    queryFn: async () => {
      if (!numericClientId) throw new Error('Client ID is required');
      
      const { data, error } = await supabase
        .from('clients')
        .select(`
          id,
          name,
          legal_name,
          short_name,
          billing_details,
          contact_info,
          projects (
            id,
            name,
            code,
            status,
            start_date,
            end_date
          )
        `)
        .eq('id', numericClientId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!numericClientId
  });

  const updateClientName = async (newName: string) => {
    if (!numericClientId) return;

    const { error } = await supabase
      .from('clients')
      .update({ name: newName })
      .eq('id', numericClientId);

    if (error) {
      toast.error("Failed to update client name");
      return;
    }

    toast.success("Client name updated successfully");
    refetch();
    setIsEditingName(false);
  };

  const handleDeleteClient = async () => {
    if (!numericClientId) return;

    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', numericClientId);

    if (error) {
      toast.error("Failed to delete client");
      return;
    }

    toast.success("Client deleted successfully");
    navigate("/clients");
  };

  const createProject = async (values: { name: string }) => {
    if (!numericClientId) return;

    const { data: { user } } = await supabase.auth.getUser();
    console.log('Authenticated User:', user);
    
    if (!user) {
      toast.error("No authenticated user");
      return;
    }

    const { data: profile } = await supabase
      .from('users')
      .select('company_id')
      .eq('user_id', user.id)
      .maybeSingle();
    
    console.log('Profile Query Response:', profile);

    if (!profile?.company_id) {
      toast.error("No company associated with your profile");
      return;
    }

    const { data, error } = await supabase
      .from('projects')
      .insert([
        {
          name: values.name,
          client_id: numericClientId,
          code: values.name.substring(0, 3).toUpperCase(),
          status: 'active',
          type: 'fixed_price'
        }
      ])
      .select()
      .single();

    if (error) {
      toast.error("Failed to create project");
      return;
    }

    toast.success("Project created successfully");
    navigate(`/projects/${data.id}`);
  };

  const updateLegalName = async (value: string) => {
    if (!numericClientId) return false;

    const { error } = await supabase
      .from('clients')
      .update({ legal_name: value })
      .eq('id', numericClientId);

    if (error) {
      toast.error(`Failed to update legal name`);
      return false;
    }

    toast.success(`Legal name updated successfully`);
    refetch();
    return true;
  };

  const updateShortName = async (value: string) => {
    if (!numericClientId) return false;

    const { error } = await supabase
      .from('clients')
      .update({ short_name: value })
      .eq('id', numericClientId);

    if (error) {
      toast.error(`Failed to update short name`);
      return false;
    }

    toast.success(`Short name updated successfully`);
    refetch();
    return true;
  };

  const updateBillingDetail = async (field: string, value: string) => {
    if (!numericClientId) return false;

    const currentDetails = client?.billing_details as Record<string, any> || {};
    const newDetails = { ...currentDetails, [field]: value };

    const { error } = await supabase
      .from('clients')
      .update({ billing_details: newDetails })
      .eq('id', numericClientId);

    if (error) {
      toast.error(`Failed to update ${field}`);
      return false;
    }

    toast.success(`${field} updated successfully`);
    refetch();
    return true;
  };

  const updateContact = async (index: number, field: string, value: string) => {
    if (!numericClientId || !client) return;

    const contacts = [...(client.contact_info as any[] || [])];
    contacts[index] = { ...contacts[index], [field]: value };

    const { error } = await supabase
      .from('clients')
      .update({ contact_info: contacts })
      .eq('id', numericClientId);

    if (error) {
      toast.error(`Failed to update contact`);
      return false;
    }

    toast.success(`Contact updated successfully`);
    refetch();
    return true;
  };

  const deleteContact = async (index: number) => {
    if (!numericClientId || !client) return;

    const contacts = [...(client.contact_info as any[] || [])];
    contacts.splice(index, 1);

    const { error } = await supabase
      .from('clients')
      .update({ contact_info: contacts })
      .eq('id', numericClientId);

    if (error) {
      toast.error("Failed to delete contact");
      return;
    }

    toast.success("Contact deleted successfully");
    refetch();
  };

  const handleAddContact = async (values: { name: string; role: string; email: string; phone?: string }) => {
    if (!numericClientId || !client) return;

    const contacts = [...(client.contact_info as any[] || [])];
    contacts.push(values);

    const { error } = await supabase
      .from('clients')
      .update({ contact_info: contacts })
      .eq('id', numericClientId);

    if (error) {
      toast.error("Failed to add contact");
      return;
    }

    toast.success("Contact added successfully");
    refetch();
    setDialogOpen(false);
  };

  const ProjectForm = () => {
    const form = useForm({
      defaultValues: {
        name: "",
      },
    });

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(createProject)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Project Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full">Create Project</Button>
        </form>
      </Form>
    );
  };

  const EditableField = ({ 
    icon: Icon, 
    label, 
    value, 
    onSave,
  }: { 
    icon: React.ElementType; 
    label: string; 
    value?: string | null;
    onSave: (value: string) => Promise<boolean>;
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value || '');

    const handleSave = async () => {
      const success = await onSave(editValue);
      if (success) {
        setIsEditing(false);
      }
    };

    return (
      <div className="flex items-start gap-2">
        <Icon className="h-5 w-5 mt-0.5 text-muted-foreground" />
        <div className="flex-1">
          <p className="font-medium">{label}</p>
          {isEditing ? (
            <div className="flex gap-2 mt-1">
              <Input 
                value={editValue} 
                onChange={(e) => setEditValue(e.target.value)}
                className="h-8"
              />
              <Button size="sm" onClick={handleSave}>Save</Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
            </div>
          ) : (
            <div 
              className="cursor-pointer hover:bg-accent hover:text-accent-foreground p-1 rounded-md -ml-1"
              onClick={() => setIsEditing(true)}
            >
              {value ? (
                <p className="text-muted-foreground">{value}</p>
              ) : (
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Minus className="h-4 w-4" />
                  <span className="text-sm">Click to add {label.toLowerCase()}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const NumericEditableField = ({ 
    icon: Icon, 
    label, 
    value, 
    onSave,
  }: { 
    icon: React.ElementType; 
    label: string; 
    value?: string | null;
    onSave: (value: string) => Promise<boolean>;
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value || '');

    const handleSave = async () => {
      const success = await onSave(editValue);
      if (success) {
        setIsEditing(false);
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === '' || /^[0-9]+$/.test(value)) {
        setEditValue(value);
      }
    };

    return (
      <div className="flex items-start gap-2">
        <Icon className="h-5 w-5 mt-0.5 text-muted-foreground" />
        <div className="flex-1">
          <p className="font-medium">{label}</p>
          {isEditing ? (
            <div className="flex gap-2 mt-1">
              <Input 
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={editValue}
                onChange={handleChange}
                className="h-8"
              />
              <Button size="sm" onClick={handleSave}>Save</Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
            </div>
          ) : (
            <div 
              className="cursor-pointer hover:bg-accent hover:text-accent-foreground p-1 rounded-md -ml-1"
              onClick={() => setIsEditing(true)}
            >
              {value ? (
                <p className="text-muted-foreground">{value}</p>
              ) : (
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Minus className="h-4 w-4" />
                  <span className="text-sm">Click to add {label.toLowerCase()}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContactCell = (contact: any, index: number, field: string) => {
    const isEditing = editingCell?.index === index && editingCell?.field === field;
    const value = contact[field];

    if (isEditing) {
      return (
        <div className="flex items-center gap-2">
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="h-8"
            onKeyDown={(e) => {
              if (e.key === "Enter") updateContact(index, field, editValue);
              if (e.key === "Escape") setEditingCell(null);
            }}
            autoFocus
          />
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => updateContact(index, field, editValue)}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setEditingCell(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      );
    }

    return (
      <div
        className="cursor-pointer hover:bg-accent hover:text-accent-foreground p-2 rounded"
        onClick={() => {
          setEditingCell({ index, field, value });
          setEditValue(value);
        }}
      >
        {value}
      </div>
    );
  };

  const handleProjectClick = (projectId: number) => {
    navigate(`/projects/${projectId}`);
  };

  if (isLoading) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-4">Client not found</h1>
      </div>
    );
  }

  const billingDetails = client.billing_details as {
    currency?: string;
    address?: string;
    taxId?: string;
    paymentTerms?: string;
  } | null;

  const contacts = (client.contact_info as {
    name: string;
    role: string;
    email: string;
    phone?: string;
  }[] | null) || [];

  return (
    <div className="p-8 space-y-6">
      {isEditingName ? (
        <div className="flex items-center gap-2">
          <Input
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            className="text-3xl font-bold h-12"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") updateClientName(nameValue);
              if (e.key === "Escape") setIsEditingName(false);
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => updateClientName(nameValue)}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsEditingName(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <h1
          className="text-3xl font-bold cursor-pointer hover:bg-accent hover:text-accent-foreground p-2 rounded-md -ml-2"
          onClick={() => {
            setNameValue(client.name);
            setIsEditingName(true);
          }}
        >
          {client.name}
        </h1>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="grid grid-cols-2 gap-4">
                <EditableField 
                  icon={Building2} 
                  label="Legal Name" 
                  value={client.legal_name} 
                  onSave={updateLegalName}
                />
                <EditableField 
                  icon={Building2} 
                  label="Short Name" 
                  value={client.short_name} 
                  onSave={updateShortName}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <EditableField 
                  icon={Receipt} 
                  label="Tax ID" 
                  value={billingDetails?.taxId} 
                  onSave={(value) => updateBillingDetail('taxId', value)}
                />
                <EditableField 
                  icon={CreditCard} 
                  label="Currency" 
                  value={billingDetails?.currency} 
                  onSave={(value) => updateBillingDetail('currency', value)}
                />
              </div>
              <EditableField 
                icon={MapPin} 
                label="Billing Address" 
                value={billingDetails?.address} 
                onSave={(value) => updateBillingDetail('address', value)}
              />
              <NumericEditableField 
                icon={Clock} 
                label="Payment Terms (days)" 
                value={billingDetails?.paymentTerms} 
                onSave={(value) => updateBillingDetail('paymentTerms', value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Contacts
            </CardTitle>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contact
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Contact</DialogTitle>
                </DialogHeader>
                <ContactForm onSubmit={handleAddContact} />
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No contacts added
                    </TableCell>
                  </TableRow>
                ) : (
                  contacts.map((contact, index) => (
                    <TableRow key={index}>
                      <TableCell>{renderContactCell(contact, index, 'name')}</TableCell>
                      <TableCell>{renderContactCell(contact, index, 'role')}</TableCell>
                      <TableCell>{renderContactCell(contact, index, 'email')}</TableCell>
                      <TableCell>{renderContactCell(contact, index, 'phone')}</TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Contact</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this contact? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteContact(index)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Projects</CardTitle>
            <Dialog>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Project
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Project</DialogTitle>
                </DialogHeader>
                <ProjectForm />
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!client.projects || client.projects.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No projects added
                    </TableCell>
                  </TableRow>
                ) : (
                  client.projects.map((project) => (
                    <TableRow 
                      key={project.id}
                      className="cursor-pointer hover:bg-accent"
                      onClick={() => handleProjectClick(project.id)}
                    >
                      <TableCell>{project.name}</TableCell>
                      <TableCell>{project.code}</TableCell>
                      <TableCell>{project.status}</TableCell>
                      <TableCell>{project.start_date}</TableCell>
                      <TableCell>{project.end_date}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Client
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the client
                and all associated data.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteClient}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
